import {attach, guard, sample} from 'effector';
import i18next from 'i18next';

import {toast} from 'react-toastify';

import {DocumentStatus} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';

import {fetchDataFx} from 'models/documents/documents/grid/table';
import {DocumentsGridRawRowExtended} from 'types/invoices';

import {errorHandlerNoFirs} from '../create/helpers/errorHandlerNoFirs';

import {fetchUnlockInvoiceCodesFx, setInvoiceCreatingPending} from '../details';

import {
    $invoiceLinesInitailGtinsHolder,
    $invoiceState,
    $invoiceStatusInfo,
    $newInvoiceIsDone,
    CreateInvoiceGate,
    deleteInvoiceFx,
    fetchCompanyNameFx,
    fetchOptionFx,
    fetchPaymentMeansFx,
    fetchTaxCategodyFx,
    handleLinesForChangeLockStatus,
    InvoiceStatusInfo,
    setInvoiceLinesInitailGtins,
    setNewInvoiceIsDone,
    setShowDeleteInvoiceModal,
    updateInvoiceBodyFx,
    updateInvoiceStatusFx,
} from './index';

deleteInvoiceFx.fail.watch(({error}) => showRestError(error as RestError));

deleteInvoiceFx.done.watch((data) => {
    const {reportId} = data.result.data;
    setShowDeleteInvoiceModal(false);
    toast.success(i18next.t('pages.documents.form.documentRemoved', {reportId}), {
        className: 'Toastify__toast__wide',
    });
});

$invoiceLinesInitailGtinsHolder.on(setInvoiceLinesInitailGtins, (_, data) => data);

const singleInvoiceFetchDataFx = attach({
    effect: fetchDataFx,
    mapParams: () => ({
        sortings: [{id: 'createdAt', desc: true}],
        filters: [],
        paging: {
            pageNumber: 0,
            pageSize: 3,
        },
        search: $invoiceState.getState() ?? '',
        updOnly: true,
    }),
});

guard({
    clock: $invoiceState,
    filter: (i: string | null) => !!i && i !== 'new',
    target: singleInvoiceFetchDataFx,
});

sample({
    clock: CreateInvoiceGate.open,
    target: [fetchOptionFx, fetchCompanyNameFx, fetchTaxCategodyFx, fetchPaymentMeansFx],
});

updateInvoiceStatusFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});

updateInvoiceStatusFx.done.watch((reportId) => {
    toast.success(i18next.t('pages.documents.invoices.statusChanged', {reportId}), {
        className: 'Toastify__toast__wide',
    });
});

$newInvoiceIsDone.on(setNewInvoiceIsDone, (_, data) => data);

sample({
    clock: [updateInvoiceStatusFx.doneData],
    fn: () => true,
    target: setNewInvoiceIsDone,
});

const invoiceStatusInfoGuard = guard({
    clock: $invoiceState,
    filter: (i: string | null) => !!i && i === 'new',
});
sample({
    clock: invoiceStatusInfoGuard,
    fn: () => ({side: 'OUTGOING', status: DocumentStatus['DRAFT']} as InvoiceStatusInfo),
    target: $invoiceStatusInfo,
});

$invoiceStatusInfo
    .on(singleInvoiceFetchDataFx.doneData, (_, data) => {
        const {side, status} = data.content[0] as DocumentsGridRawRowExtended;
        return {
            side,
            status,
        };
    })
    .reset(CreateInvoiceGate.close);

sample({
    clock: deleteInvoiceFx.doneData,
    fn: () =>
        handleLinesForChangeLockStatus({
            toLock: false,
        }) as string[],
    target: fetchUnlockInvoiceCodesFx,
});

updateInvoiceBodyFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});

updateInvoiceBodyFx.done.watch((data) => {
    const {reportId} = data.result.data.data;
    toast.success(i18next.t('pages.documents.form.documentUpdated', {number: reportId}), {
        className: 'Toastify__toast__wide',
    });

    setInvoiceCreatingPending(true);
    setNewInvoiceIsDone(true);
});
